import { CheckCircle, Clock, XCircle, Calendar, Home, Plane, Minus, Landmark, CalendarClock } from 'lucide-react';
import type { MatrixCellStatus } from '../../types/attendanceGrid.types';

/**
 * Canonical attendance status → colour / icon / label map.
 *
 * The six shared statuses are lifted verbatim from the private STATUS_CONFIG that
 * used to live in components/dashboard/StaffAttendanceRow.tsx — the most complete
 * map in the codebase, but not importable.
 *
 * KNOWN DIVERGENT COPIES, deliberately left alone: pages/People.tsx,
 * pages/History.tsx and components/reports/AttendanceReport.tsx each carry their
 * own palette, and they genuinely differ (present is #2ECC8F in People but #16A34A
 * in the report; the WFH and travel hues are transposed between them). Unifying
 * them is a visual change to three unrelated pages with no snapshot coverage — a
 * separate, deliberate decision, not a side effect of adding a grid.
 *
 * Lives in its own module rather than beside StatusChip so that file exports only
 * components (react-refresh/only-export-components).
 */

export interface StatusConfig {
  bg: string;
  color: string;
  icon: typeof CheckCircle;
  label: string;
  /** Compact form for narrow matrix columns. */
  short: string;
}

/**
 * Colours are CSS variables rather than hex so the grid follows dark mode. `bg`
 * is a tint mixed toward the page background; `color` is the saturated token,
 * which keeps text legible against its own tint in both themes.
 */
const tint = (token: string, pct = 12) =>
  `color-mix(in oklab, var(${token}) ${pct}%, var(--background))`;

export const STATUS_CONFIG: Record<MatrixCellStatus, StatusConfig> = {
  present: { bg: tint('--success'), color: 'var(--success)', icon: CheckCircle, label: 'Present', short: 'In' },
  late: { bg: tint('--warning'), color: 'var(--warning)', icon: Clock, label: 'Late', short: 'Late' },
  absent: { bg: tint('--destructive'), color: 'var(--destructive)', icon: XCircle, label: 'Absent', short: 'Abs' },
  on_leave: { bg: tint('--info'), color: 'var(--info)', icon: Calendar, label: 'On Leave', short: 'Lve' },
  wfh: { bg: tint('--chart-4'), color: 'var(--chart-4)', icon: Home, label: 'WFH', short: 'WFH' },
  // chart-5 rather than --info: travel sat next to on_leave on the same blue,
  // so the two were hard to tell apart at token size in the matrix.
  travel: { bg: tint('--chart-5'), color: 'var(--chart-5)', icon: Plane, label: 'Travelling', short: 'Trv' },
  // Grid-only members. These two need a visible background: with no attendance
  // recorded yet every cell is one of them, and near-white-on-white made the whole
  // matrix look like it had failed to render.
  not_yet: { bg: 'var(--muted)', color: 'var(--muted-foreground)', icon: CalendarClock, label: 'Not yet clocked in', short: 'Due' },
  future: { bg: tint('--muted', 50), color: 'var(--muted-foreground)', icon: Minus, label: 'Upcoming', short: '·' },
  weekend: { bg: 'var(--muted)', color: 'var(--muted-foreground)', icon: Minus, label: 'Weekend', short: '' },
  holiday: { bg: 'var(--muted)', color: 'var(--muted-foreground)', icon: Landmark, label: 'Public holiday', short: '' },
};
